/**
 * How one payment in a multi-payment cart is named to a payer.
 *
 * The cart slider is where a payer meets these groups, so its heading is the only name
 * they can act on. Anything else - a Teller connection's name, a system code - is
 * configuration they have never seen: a notice reading "Dothan.On Credit Card payments
 * must be at least $100.00" named a payment the cart called "Liquor".
 *
 * One function, called by both the cart slider and the checkout notices, so the two
 * cannot drift apart.
 */

/** The little of a cart item these helpers depend on. Structural, to avoid importing
 *  the cart service into a helper the cart service's own consumers import. */
export interface PartitionedItem {
    page?: { pageId?: number; title?: string };
    paymentOptions?: { isCustomAmount?: boolean }[];
}

/**
 * The shared page title of a payment's items, or undefined when they have none in common.
 *
 * Undefined is a real answer, and each caller decides what to show for it: the cart slider
 * shows "Other", while a notice drops the name entirely rather than opening a sentence
 * with "Other Credit Card payments...".
 */
export function cartPartitionHeading(
    items: readonly PartitionedItem[],
): string | undefined {
    const titles = new Set((items ?? []).map(item => item.page?.title));

    if (titles.size !== 1) {
        return undefined;
    }

    return titles.values().next().value || undefined;
}

export type CartPartitions = { [key: string]: { items: PartitionedItem[] } };

/** The payment holding the given page, or undefined when no payment does. */
export function findCartPartitionForPage(
    partitions: CartPartitions | undefined,
    pageId: number | undefined,
): { items: PartitionedItem[] } | undefined {
    if (!pageId) {
        return undefined;
    }

    return Object.values(partitions ?? {}).find(
        group => group.items?.some(item => item.page?.pageId === pageId));
}

/**
 * The heading of the payment holding the given page, for a cart of more than one payment.
 *
 * Undefined for a single payment, matching the slider: with one payment there is nothing
 * to tell apart, and it shows no heading at all.
 */
export function cartPartitionHeadingForPage(
    partitions: CartPartitions | undefined,
    pageId: number | undefined,
): string | undefined {
    if (Object.keys(partitions ?? {}).length < 2) {
        return undefined;
    }

    const holding = findCartPartitionForPage(partitions, pageId);

    return holding ? cartPartitionHeading(holding.items) : undefined;
}

/**
 * Whether the payment holding this page has an item the payer can pay less on.
 *
 * A cap is cleared by removing items - or, where an item offers a custom amount, by
 * lowering what is being paid on it, which is the gentler remedy and the one a payer
 * would rather hear. Read from the options the item OFFERS, not the one it was added
 * with: an invoice added as pay-in-full that still allows a partial payment qualifies.
 *
 * Items with no payment options at all - misc and Teller item types - are fixed amounts,
 * so they correctly do not qualify. A custom option carries its own minimum, so lowering
 * is not unbounded; this is guidance towards an action, not a promise that any amount
 * will fit under the cap.
 */
export function cartPartitionAllowsCustomAmount(
    partitions: CartPartitions | undefined,
    pageId: number | undefined,
): boolean {
    const holding = findCartPartitionForPage(partitions, pageId);

    return !!holding?.items?.some(
        item => item.paymentOptions?.some(option => option.isCustomAmount));
}
